import React from 'react';
import kebabCase from 'lodash/kebabCase';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import AnimatedArcs from '../AnimatedArcs';

export interface IVerticalTextGalleryFeatureProps extends IBaseElementProps {
	/**
	 * Theme - Default: Light
	 */
	theme?: 'Light' | 'Dark';
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Description
	 * @control richtext
	 */
	description?: string;
	/**
	 * CTA Button
	 */
	ctaButton?: ILink;
	/**
	 * Quote
	 */
	quote?: string;
	hideQuotationMark?: boolean;
	/**
	 * Quote Author
	 */
	quoteAuthor?: string;
	/**
	 * Show opening and closing quote. Default shows only the opening quote.
	 */
	twoQuotes?: boolean;
	/**
	 * Image One - Recommended Size: 400px x 400px
	 */
	imageOne?: IImage;
	/**
	 * Image Two - Recommended Size: 300px x 400px
	 */
	imageTwo?: IImage;
	/**
	 * Image Three - Recommended Size: 225px x 400px
	 */
	imageThree?: IImage;
	/**
	 * Enable Line Animations
	 */
	enableLineAnimations?: boolean;
	/**
	 * Line Animation Delay in Milliseconds
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels
	 */
	lineAnimationOffset?: number;
	/**
	 * Arc One Color - HEX Value
	 */
	arcOneColor?: string;
	/**
	 * Arc One Stroke Width
	 * Default: 3
	 */
	arcOneStrokeWidth?: number;
	/**
	 * Arc Two Color - HEX Value
	 */
	arcTwoColor?: string;
	/**
	 * Arc Two Stroke Width
	 * Default: 3
	 */
	arcTwoStrokeWidth?: number;
	inverted?: boolean;
}

/**
 * Calian Vertical Text Gallery Component
 * @icon format-gallery
 * @block
 */
const VerticalTextGalleryFeature: React.FC<IVerticalTextGalleryFeatureProps> =
	({
		id,
		className,
		classNames = [],
		theme,
		heading,
		description,
		ctaButton,
		quote,
		quoteAuthor,
		twoQuotes,
		imageOne,
		imageTwo,
		imageThree,
		enableLineAnimations,
		lineAnimationDelay,
		lineAnimationOffset,
		arcOneColor,
		arcOneStrokeWidth,
		arcTwoColor,
		arcTwoStrokeWidth,
		inverted = false,
		hideQuotationMark = false,
	}) => (
		<div
			id={id}
			className={
				className ||
				cn('calian-vertical-text-gallery-feature overflow-hidden', {
					[`theme-${kebabCase(theme)}`]: theme,
					[`calian-vertical-text-gallery-feature--inverted`]:
						inverted,
					[`calian-vertical-text-gallery-feature--no-quote`]:
						hideQuotationMark,
					...classNames,
				})
			}
		>
			<div className="calian-vertical-text-gallery-feature__container container-fluid">
				<div className="calian-vertical-text-gallery-feature__top-content row">
					<div className="calian-vertical-text-gallery-feature__top-content--left col-12 col-lg-6">
						{heading && (
							<h2 className="calian-vertical-text-gallery-feature__heading">
								{heading}
							</h2>
						)}
						{description && (
							<div
								className="calian-vertical-text-gallery-feature__description"
								dangerouslySetInnerHTML={{
									__html: description,
								}}
							/>
						)}
						{ctaButton && (ctaButton.href || ctaButton.label) && (
							<Button
								{...ctaButton}
								className={`btn btn-${
									theme === 'Light' && inverted
										? 'secondary'
										: 'primary'
								} calian-vertical-text-gallery-feature__cta-btn`}
							/>
						)}
					</div>
					<div className="calian-vertical-text-gallery-feature__top-content--right col-12 col-lg-6">
						{quote && (
							<div
								className={`calian-vertical-text-gallery-feature__quote${
									twoQuotes ? ' show-two-quotes' : ''
								}`}
							>
								<p>{quote}</p>
								{quoteAuthor && (
									<span className="calian-vertical-text-gallery-feature__quote-author d-block pt-md-2 pt-3 ps-4 ps-sm-0 fs-7 fs-md-5">
										- {quoteAuthor}
									</span>
								)}
							</div>
						)}
					</div>
				</div>
				<div className="row">
					<div className="col-12">
						<div className="calian-vertical-text-gallery-feature__image-container">
							{imageOne && (
								<div className="calian-vertical-text-gallery-feature__image-one">
									<Image
										{...imageOne}
										className="img-fluid"
									/>
								</div>
							)}
							{imageTwo && (
								<div className="calian-vertical-text-gallery-feature__image-two">
									<Image
										{...imageTwo}
										className="img-fluid"
									/>
								</div>
							)}
							{imageThree && (
								<div className="calian-vertical-text-gallery-feature__image-three">
									<Image
										{...imageThree}
										className="img-fluid"
									/>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<AnimatedArcs
				arcsStyle={'Component - Two Lines'}
				arcsPosition="Right"
				arcOneColor={arcOneColor}
				arcOneStrokeWidth={arcOneStrokeWidth}
				arcTwoColor={arcTwoColor}
				arcTwoStrokeWidth={arcTwoStrokeWidth}
				animationDelay={lineAnimationDelay}
				animationOffset={lineAnimationOffset}
				disableAnimation={!enableLineAnimations}
			/>
		</div>
	);

export default VerticalTextGalleryFeature;
